import { Avatar, Box, Menu, MenuItem } from "@mui/material";
import { DotLegalTooltip } from "@dotlegal/dotlegal-ui-components";
import React, { useState } from "react";
import { useTranslation } from "../../../localization/useTranslation.ts";
import { useUserContext } from "../../../auth/userContextProvider/UserContextProvider.tsx";
import AddIcon from "@mui/icons-material/Add";
import {
    GetContractsPlanLimit,
    GetPolicyPlanLimit,
    GetProcessingActivityPlanLimit,
    GetProcessPlanLimit,
    GetSystemsPlanLimit,
} from "../../../auth/planProvider/PlanProvider.hooks.ts";
import { TrackingEvent, useTrackAIEvent } from "../../../processingActivity/hooks/useTracking.tsx";
import AddProcessingActivityPane from "../../../processingActivity/addProcessingActivityPane/AddProcessingActivityPane";
import AddSystemPane from "../../../system/addSystemPane/AddSystemPane";
import { useNavigate } from "react-router";
import { useUrlProvider } from "../../../useUrlProvider.ts";
import { useFeatureFlags, usePlanContext } from "../../../auth/planProvider/PlanProvider.tsx";
import { FeatureFlag } from "../../../auth/planProvider/PlanProvider.types.ts";
import PlanDialogButtonWrapper from "../../../plan/planDialogWrapper/planDialogWrapper.tsx";
import AddEditPolicyPane from "../../../policy/addEditPolicyPane/AddEditPolicyPane";
import { LegalEntityScope } from "../../../legalEntity/legalEntities/LegalEntities.types.ts";
import AddLegalEntityPane from "../../../legalEntity/addLegalEntityPane/AddLegalEntityPane";
import { usePanes } from "../../panes/usePanes";
import CreateTaskPane from "../../../taskManagement/createTaskPane/CreateTaskPane";
import CreateContractPane from "../../../contract/addContractButton/createContractPane/CreateContractPane";
import AddProcessPane from "../../../process/addProcessPane/AddProcessPane";
import { useDotLegalQueryClient } from "@common/hooks/dotLegalReactQuery";

function CreateNewMenuItem() {
    const trackAIEvent = useTrackAIEvent();
    const navigate = useNavigate();
    const queryClient = useDotLegalQueryClient();

    const { open } = usePanes();

    // This menu has no list of its own behind it, so nothing here registers with the panel. The blunt
    // refetch it has always done is what keeps the overdue badge and the dashboard counters honest, and it
    // now rides along with the created task instead of the dialog that used to close.
    function openCreateTask() {
        open(CreateTaskPane, {
            onCreated: () =>
                queryClient.refetchQueriesByPredicate((query) =>
                    query.queryKey.some((key) => typeof key === "string" && (key.includes("task") || key.includes("relations")))
                ),
        });
    }
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const isOpen = Boolean(anchorEl);

    const { platformFeatures, informationSecurityFeatures, dataProtectionFeatures, contractManagementFeatures } = usePlanContext();
    const { permissions } = useUserContext();
    const { translateString } = useTranslation();
    const { getSystemCreateViewUrl } = useUrlProvider();
    const featureFlags = useFeatureFlags();

    const { data: processingActivityPlanLimitData } = GetProcessingActivityPlanLimit(permissions.processingActivityPermissions.create && isOpen);
    const { data: systemsPlanLimitData } = GetSystemsPlanLimit(permissions.systemPermissions.create && isOpen);
    const { data: policyPlanLimitData, refetch: refetchPolicyLimitData } = GetPolicyPlanLimit(permissions.policyPermissions.create && isOpen);
    const { contractsPlanLimitData } = GetContractsPlanLimit(permissions.contractManagementPermissions.permissions.create && isOpen);
    const canCreateProcess = featureFlags.isEnabled(FeatureFlag.ProcessTemplates) && permissions.processPermissions.create;
    const { data: processPlanLimitData } = GetProcessPlanLimit(canCreateProcess && isOpen);

    const getMenuItems = () => {
        const menuItems: Array<React.ReactNode> = [];

        const disableProcessingActivity = processingActivityPlanLimitData !== undefined && processingActivityPlanLimitData.limitReached;
        if (permissions.processingActivityPermissions.create) {
            menuItems.push(
                <PlanDialogButtonWrapper
                    enabled={disableProcessingActivity}
                    text={translateString("processingActivityLimitReached", { count: informationSecurityFeatures.maxNumberOfSystems })}
                    key={"create-processing-activity"}
                >
                    <DotLegalTooltip
                        text={
                            disableProcessingActivity
                                ? translateString("processingActivityLimitReached", {
                                      count: dataProtectionFeatures.maxNumberOfProcessingActivities,
                                  })
                                : ""
                        }
                        position={"right"}
                    >
                        <Box component={"span"}>
                            <MenuItem
                                onClick={() => {
                                    trackAIEvent(TrackingEvent.AddProcessingActivityClicked);
                                    open(AddProcessingActivityPane, {
                                        shouldDisplayCreateProcessingActivityWarning:
                                            processingActivityPlanLimitData !== undefined && processingActivityPlanLimitData?.warnLimitReachedOnTrial,
                                    });
                                    setAnchorEl(null);
                                }}
                                disabled={disableProcessingActivity}
                            >
                                {translateString("processingActivity")}
                            </MenuItem>
                        </Box>
                    </DotLegalTooltip>
                </PlanDialogButtonWrapper>
            );
        }

        const disableSystem = systemsPlanLimitData !== undefined && systemsPlanLimitData.limitReached;
        if (permissions.systemPermissions.create) {
            menuItems.push(
                <PlanDialogButtonWrapper
                    enabled={disableSystem}
                    text={translateString("systemLimitReached", { count: informationSecurityFeatures.maxNumberOfSystems })}
                    key={"create-system"}
                >
                    <DotLegalTooltip
                        text={disableSystem ? translateString("systemLimitReached", { count: informationSecurityFeatures.maxNumberOfSystems }) : ""}
                        position={"right"}
                    >
                        <Box component={"span"}>
                            <MenuItem
                                onClick={() => {
                                    trackAIEvent(TrackingEvent.AddSystemClicked);
                                    open(AddSystemPane, {
                                        shouldDisplayCreateSystemWarning: systemsPlanLimitData?.warnLimitReachedOnTrial,
                                        onCreated: (id) => navigate(getSystemCreateViewUrl(id)),
                                    });
                                    setAnchorEl(null);
                                }}
                                disabled={disableSystem}
                            >
                                {translateString("system")}
                            </MenuItem>
                        </Box>
                    </DotLegalTooltip>
                </PlanDialogButtonWrapper>
            );
        }

        const disableContracts = contractsPlanLimitData !== undefined && contractsPlanLimitData.limitReached;
        if (permissions.contractManagementPermissions.permissions.create) {
            menuItems.push(
                <PlanDialogButtonWrapper
                    enabled={disableContracts}
                    text={translateString("contractLimitReached", { count: contractManagementFeatures.maxNumberOfContracts })}
                    key={"create-contract"}
                >
                    <DotLegalTooltip
                        text={disableContracts ? translateString("contractLimitReached", { count: contractManagementFeatures.maxNumberOfContracts }) : ""}
                        position={"right"}
                    >
                        <Box component={"span"}>
                            <MenuItem
                                onClick={() => {
                                    open(CreateContractPane, {});
                                    setAnchorEl(null);
                                }}
                                disabled={disableContracts}
                            >
                                {translateString("contract")}
                            </MenuItem>
                        </Box>
                    </DotLegalTooltip>
                </PlanDialogButtonWrapper>
            );
        }

        if (permissions.taskPermissions.create) {
            menuItems.push(
                <MenuItem
                    key={"create-task"}
                    onClick={() => {
                        trackAIEvent(TrackingEvent.AddAdHocTask);
                        openCreateTask();
                        setAnchorEl(null);
                    }}
                >
                    {translateString("task")}
                </MenuItem>
            );
        }

        const disablePolicy = policyPlanLimitData !== undefined && policyPlanLimitData.limitReached;
        if (permissions.policyPermissions.create) {
            menuItems.push(
                <PlanDialogButtonWrapper
                    text={translateString("policyLimitReached", { count: platformFeatures.maxNumberOfPolicies })}
                    enabled={disablePolicy}
                    key={"create-policy"}
                >
                    <DotLegalTooltip
                        text={disablePolicy ? translateString("policyLimitReached", { count: informationSecurityFeatures.maxNumberOfSystems }) : ""}
                        position={"right"}
                    >
                        <Box component={"span"}>
                            <MenuItem
                                onClick={() => {
                                    trackAIEvent(TrackingEvent.AddPolicyClicked);
                                    open(AddEditPolicyPane, {});
                                    setAnchorEl(null);
                                }}
                                disabled={disablePolicy}
                            >
                                {translateString("policy")}
                            </MenuItem>
                        </Box>
                    </DotLegalTooltip>
                </PlanDialogButtonWrapper>
            );
        }

        if (permissions.companyPermissions.create) {
            menuItems.push(
                <MenuItem
                    key={"create-legal-entity"}
                    onClick={() => {
                        trackAIEvent(TrackingEvent.AddVendorClicked);
                        open(AddLegalEntityPane, { legalEntityScope: LegalEntityScope.External });
                        setAnchorEl(null);
                    }}
                >
                    {translateString("legalEntity")}
                </MenuItem>
            );
        }

        if (canCreateProcess) {
            const disableProcess = processPlanLimitData !== undefined && processPlanLimitData.limitReached;
            const processLimitReachedText = translateString("processLimitReached", { count: platformFeatures.maxNumberOfProcesses });
            menuItems.push(
                <PlanDialogButtonWrapper enabled={disableProcess} text={processLimitReachedText} key={"create-process"}>
                    <DotLegalTooltip text={disableProcess ? processLimitReachedText : ""} position={"right"}>
                        <Box component={"span"}>
                            <MenuItem
                                onClick={() => {
                                    open(AddProcessPane, {
                                        shouldDisplayCreateProcessWarning: processPlanLimitData !== undefined && processPlanLimitData.warnLimitReachedOnTrial,
                                    });
                                    setAnchorEl(null);
                                }}
                                disabled={disableProcess}
                            >
                                {translateString("process")}
                            </MenuItem>
                        </Box>
                    </DotLegalTooltip>
                </PlanDialogButtonWrapper>
            );
        }

        return menuItems;
    };

    const menuItems = getMenuItems();

    return (
        <>
            {menuItems.length > 0 && (
                <>
                    <DotLegalTooltip text={translateString("createNew")}>
                        <Avatar
                            sx={(theme) => ({
                                backgroundColor: "#eeeef9",
                                color: theme.palette.primary.main,
                                height: 30,
                                width: 30,
                                fontSize: 14,
                                "&:hover": { cursor: "pointer" },
                            })}
                            onClick={(e) => {
                                setAnchorEl(e.currentTarget);
                            }}
                        >
                            <AddIcon />
                        </Avatar>
                    </DotLegalTooltip>

                    <Menu
                        sx={{
                            "& .MuiMenu-paper": {
                                width: 250,
                                padding: 1,
                                marginTop: 1,
                            },
                        }}
                        anchorEl={anchorEl}
                        open={isOpen}
                        onClose={() => setAnchorEl(null)}
                    >
                        {menuItems.map((x) => x)}
                    </Menu>
                </>
            )}
        </>
    );
}

export default CreateNewMenuItem;
